import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useHabits } from '@/hooks/useHabits';
import { useGamification } from '@/hooks/useGamification';
import { HabitList, HabitForm } from '@/components/habits';
import { CATEGORY_ICONS, CATEGORY_GRADIENTS } from '@/lib/category-icons';
import {
  LevelProgress,
  XPBar,
  AchievementToast,
  AchievementGrid,
} from '@/components/gamification';
import type { HabitWithStreak, CreateHabitInput, UpdateHabitInput } from '@/types';

type Tab = 'habits' | 'achievements';

export function HabitDashboard() {
  const {
    habits,
    isLoading: habitsLoading,
    error: habitsError,
    createHabit,
    updateHabit,
    deleteHabit,
    completeHabit,
    todayProgress,
  } = useHabits();

  const {
    stats,
    achievements,
    unlockedAchievements,
    recentUnlocks,
    isLoading: statsLoading,
    levelProgress,
    clearRecentUnlocks,
    categoryProgress,
  } = useGamification();

  const [activeTab, setActiveTab] = useState<Tab>('habits');
  const [showForm, setShowForm] = useState(false);
  const [editingHabit, setEditingHabit] = useState<HabitWithStreak | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleCreateHabit(input: CreateHabitInput | UpdateHabitInput) {
    setIsSubmitting(true);
    try {
      if (editingHabit) {
        await updateHabit(editingHabit.id, input as UpdateHabitInput);
      } else {
        await createHabit(input as CreateHabitInput);
      }
      setShowForm(false);
      setEditingHabit(null);
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleEdit(habit: HabitWithStreak) {
    setEditingHabit(habit);
    setShowForm(true);
  }

  function handleCloseForm() {
    setShowForm(false);
    setEditingHabit(null);
  }

  // Calculate XP needed for levels
  const xpForLevel = (level: number) => Math.pow(level - 1, 2) * 100;
  const currentLevelXp = stats ? xpForLevel(stats.currentLevel) : 0;
  const nextLevelXp = stats ? xpForLevel(stats.currentLevel + 1) : 100;

  const isLoading = habitsLoading || statsLoading;

  return (
    <div className="flex flex-col h-full">
      {/* Header with Stats */}
      <div className="glass-strong rounded-2xl p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          {/* Left: Level & XP */}
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-white mb-4">Habit Tracker</h1>
            {stats && (
              <div className="space-y-3">
                <LevelProgress
                  level={stats.currentLevel}
                  totalXp={stats.totalXp}
                  progress={levelProgress}
                />
                <XPBar
                  currentXp={stats.totalXp}
                  xpForCurrentLevel={currentLevelXp}
                  xpForNextLevel={nextLevelXp}
                  progress={levelProgress}
                />
              </div>
            )}
          </div>

          {/* Right: Today's Progress */}
          <div className="lg:w-64">
            <div className="p-4 rounded-xl bg-charcoal-800/50 border border-charcoal-700/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-400">Today's Progress</span>
                <span className="text-2xl font-bold text-white">
                  {todayProgress.percentage}%
                </span>
              </div>
              <div className="h-2 bg-charcoal-900 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${todayProgress.percentage}%` }}
                  transition={{ duration: 0.5 }}
                  className="h-full bg-royal-gradient"
                />
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {todayProgress.completed} of {todayProgress.total} daily habits completed
              </p>
            </div>
          </div>
        </div>

        {/* Category XP Bars */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            {(['faith', 'family', 'finance', 'fitness'] as const).map((cat) => {
              const progress = categoryProgress[cat];
              const colors = CATEGORY_GRADIENTS;

              return (
                <div key={cat} className="p-3 rounded-xl bg-charcoal-800/30">
                  <div className="flex items-center gap-2 mb-2">
                    {(() => { const Icon = CATEGORY_ICONS[cat]; return Icon ? <Icon className="w-4 h-4 text-slate-300" /> : null; })()}
                    <span className="text-sm text-slate-300 capitalize">{cat}</span>
                    <span className="ml-auto text-xs text-slate-500">{progress.xp} XP</span>
                  </div>
                  <div className="h-1.5 bg-charcoal-900 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${progress.percentage}%` }}
                      transition={{ duration: 0.5, delay: 0.1 }}
                      className={`h-full bg-gradient-to-r ${colors[cat]}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-6">
        <div className="flex bg-charcoal-800 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('habits')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'habits'
                ? 'bg-charcoal-700 text-white'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Habits
          </button>
          <button
            onClick={() => setActiveTab('achievements')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'achievements'
                ? 'bg-charcoal-700 text-white'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Achievements
            {unlockedAchievements.length > 0 && (
              <span className="ml-2 px-1.5 py-0.5 text-xs rounded bg-amber-500/20 text-amber-400">
                {unlockedAchievements.length}
              </span>
            )}
          </button>
        </div>

        {activeTab === 'habits' && (
          <button
            onClick={() => setShowForm(true)}
            className="ml-auto px-4 py-2 rounded-lg bg-royal-gradient text-white text-sm font-medium
                     shadow-glow hover:opacity-90 transition-all flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Habit
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : habitsError ? (
          <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400">
            {habitsError}
          </div>
        ) : activeTab === 'habits' ? (
          <HabitList
            habits={habits}
            onComplete={completeHabit}
            onEdit={handleEdit}
            onDelete={deleteHabit}
            groupByCategory
          />
        ) : (
          <AchievementGrid
            achievements={achievements}
            unlockedAchievements={unlockedAchievements}
          />
        )}
      </div>

      {/* Habit Form Modal */}
      <AnimatePresence>
        {showForm && (
          <HabitForm
            habit={editingHabit ?? undefined}
            onSubmit={handleCreateHabit}
            onCancel={handleCloseForm}
            isSubmitting={isSubmitting}
          />
        )}
      </AnimatePresence>

      {/* Achievement Toast */}
      <AchievementToast
        achievements={recentUnlocks}
        onDismiss={clearRecentUnlocks}
      />
    </div>
  );
}
